import { useState, useEffect, useRef } from "react";
import {
  GenderMale,
  GenderFemale,
  GenderIntersex,
  CaretDown,
} from "@phosphor-icons/react";

const GENDER_OPTIONS = [
  { value: "male", label: "זכר", icon: GenderMale },
  { value: "female", label: "נקבה", icon: GenderFemale },
  { value: "other", label: "אחר", icon: GenderIntersex },
];

/**
 * Custom select-style gender field (trigger + dropdown panel).
 * Follows the outside-click dropdown pattern from components/ui/TimePicker.
 * Moved unchanged from the old tabbed CreatePatientPage.
 */
function GenderSelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  const selected = GENDER_OPTIONS.find((option) => option.value === value);
  const SelectedIcon = selected?.icon;

  return (
    <div className="gender-select" ref={containerRef}>
      <button
        type="button"
        className={`gender-select-trigger ${isOpen ? "open" : ""} ${
          selected ? "" : "empty"
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setIsOpen(false);
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="gender-select-value">
          {SelectedIcon && (
            <SelectedIcon
              size={14}
              weight="fill"
              className="gender-select-icon"
            />
          )}
          <span>{selected ? selected.label : "בחר מגדר"}</span>
        </span>
        <CaretDown
          size={14}
          weight="bold"
          className={`gender-select-caret ${isOpen ? "open" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="gender-select-panel" role="listbox">
          {GENDER_OPTIONS.map((option) => {
            const OptionIcon = option.icon;
            const isSelected = value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`gender-select-option ${
                  isSelected ? "selected" : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <OptionIcon
                  size={14}
                  weight="fill"
                  className="gender-select-icon"
                />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const GENDER_LABELS = { male: "זכר", female: "נקבה", other: "אחר" };

export default GenderSelect;
